import { pipe } from 'fp-ts/lib/function'
import { FileWithPath } from 'react-dropzone'
import * as RA from 'fp-ts/ReadonlyArray'
import { err, errAsync, ok, okAsync, Result, ResultAsync } from 'neverthrow'
import { createFolder, putFileToSignedURL, startProcessingDoc } from '../api'
import {
  FailedToCreateFolderError,
  FailedToUploadFileError,
  MissingAuthTokenError,
} from '../errors'
import { CreateRecordBatchResponse, FileRecord, FileStatus } from '../types'
import { getUserDetailsFromToken, refetchToken } from './cookies'
import {
  removeLocalFileRecordWithIds,
  saveFileRecordsToLocal,
} from '../workbook'
import { getOfficeApiError } from './office-api-error-util'

type FolderItem = {
  path: string
  parentFolderId: string | undefined
}

type SignedUrl = {
  signedUrl: string
  id: string
}

export const createFolderResultAsync = (
  parentFolderId: string | undefined,
  folderName: string
) =>
  ResultAsync.fromPromise(
    createFolder(parentFolderId, folderName),
    () => new FailedToCreateFolderError()
  )

export const createFolderFromPath = async (
  paths: Readonly<string[]>,
  userId: string,
  parentFolderId: string | undefined
): Promise<Result<[FileRecord[], FolderItem[]], FileRecord[]>> => {
  const createdFolderPathMap = new Map<string, string>()
  const parentFolderIdItems: {
    path: string
    parentFolderId: string | undefined
  }[] = []
  const fileRecord: FileRecord[] = []
  for (const path of paths) {
    let ppfid: string | undefined = parentFolderId
    const arr = path.split('/')
    const folderNames = arr.slice(1, arr.length - 1)
    if (folderNames.length > 0) {
      let folderPath = '/'
      for (const folderName of folderNames) {
        folderPath += `${folderName}/`
        if (createdFolderPathMap.has(folderPath)) {
          ppfid = createdFolderPathMap.get(folderPath)
        } else {
          const createFolderResult = await createFolderResultAsync(
            ppfid,
            folderName
          )

          if (createFolderResult.isErr()) {
            return err(fileRecord)
          }

          const folderId = createFolderResult.value

          fileRecord.push({
            fileId: folderId,
            userId: userId,
            status: FileStatus.SUCCEEDED,
          })

          ppfid = folderId

          createdFolderPathMap.set(folderPath, ppfid)
        }
      }
    }
    parentFolderIdItems.push({
      path,
      parentFolderId: ppfid,
    })
  }
  return ok([fileRecord, parentFolderIdItems])
}

export const getFileRecordsAndSignedUrls = (
  res: CreateRecordBatchResponse,
  userId: string
) =>
  pipe(
    res.data,
    RA.reduce(
      [[], []] as [FileRecord[], SignedUrl[]],
      (acc, { fileId, uploadURL }) => {
        const [fileRecords, signedUrls] = acc
        const record = {
          fileId: fileId,
          userId,
          status: FileStatus.PROCESSING,
          isLocal: false,
        }
        const url = { id: fileId, signedUrl: uploadURL }
        return [
          [...fileRecords, record],
          [...signedUrls, url],
        ] as [FileRecord[], SignedUrl[]]
      }
    )
  )

export const getUserInfo = () =>
  getUserDetailsFromToken()
    .asyncAndThen((token) => okAsync(token))
    .orElse(() => refetchToken().andThen(getUserDetailsFromToken))
    .orElse(() => errAsync(new MissingAuthTokenError()))

export const saveFileRecordsToLocalResultAsync = (fileRecords: FileRecord[]) =>
  ResultAsync.fromPromise(saveFileRecordsToLocal(fileRecords), (err) =>
    getOfficeApiError(err)
  )

const uploadFile = async (
  signedUrlObj: Readonly<SignedUrl>,
  file: Readonly<FileWithPath>
) => {
  const { id, signedUrl } = signedUrlObj
  await putFileToSignedURL(signedUrl, file)
  await startProcessingDoc(id, file.type)
}
export const uploadFileResultAsync = (
  signedUrlObj: Readonly<SignedUrl>,
  file: Readonly<FileWithPath>
) =>
  ResultAsync.fromPromise(
    uploadFile(signedUrlObj, file),
    () => new FailedToUploadFileError(signedUrlObj.id)
  )

const removeLocalFileRecordWithIdsHelper = (fileIds: string[]) => {
  if (!fileIds.length) return Promise.resolve()
  return removeLocalFileRecordWithIds(fileIds)
}

export const removeLocalFileRecordResultAsync = (fileIds: string[]) =>
  ResultAsync.fromPromise(removeLocalFileRecordWithIdsHelper(fileIds), (err) =>
    getOfficeApiError(err)
  )
